import { useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { ArrowUp } from 'lucide-react';
import { useTheme } from '@course-authoring/shared-types';
import { useAutoGrowTextarea } from '../../hooks/useAutoGrowTextarea';
import { COMPOSER_COPY } from './authorWithAiCopy';
import { createAuthorWithAiStyles } from './authorWithAiStyles';

export interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string) => void;
  /** Planning, idle, and no send in flight. */
  enabled: boolean;
  sending: boolean;
  chipsShown: boolean;
  /** Why the composer is disabled, or an inline admission error. */
  footer: string | null;
  footerIsError?: boolean;
  /** A completed build: the bar stays but holds the footer message instead of the field and send button. */
  finished?: boolean;
}

/** Enter sends, Shift+Enter inserts a newline; the text stays until the server accepts it. */
export function Composer({ value, onChange, onSend, enabled, sending, chipsShown, footer, footerIsError = false, finished = false }: ComposerProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  const ref = useAutoGrowTextarea(value);
  const [focused, setFocused] = useState(false);
  const canSend = enabled && !sending && value.trim().length > 0;

  const send = (event?: FormEvent) => {
    event?.preventDefault();
    if (canSend) onSend(value.trim());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };

  if (finished) {
    return (
      <div style={styles.composerArea}>
        <div style={{ ...styles.composerInner, maxWidth: '712px' }}>
          <div style={{ ...styles.composer, ...styles.composerFinished }} role="status">{footer}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.composerArea}>
      <form style={styles.composerInner} onSubmit={send}>
        <div style={{ ...styles.composer, ...(focused ? styles.barFocused : {}) }}>
          <textarea
            ref={ref}
            rows={1}
            style={styles.composerInput}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={chipsShown ? COMPOSER_COPY.placeholderWithChips : COMPOSER_COPY.placeholder}
            aria-label={COMPOSER_COPY.label}
            disabled={!enabled}
          />
          <button
            type="submit"
            style={{
              ...styles.iconButton,
              width: '36px',
              height: '36px',
              ...(canSend ? styles.iconButtonActive : styles.buttonDisabled),
            }}
            disabled={!canSend}
            aria-label={COMPOSER_COPY.send}
          >
            <ArrowUp size="1em" strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
        {footer && <p style={footerIsError ? styles.error : styles.composerFooter} role={footerIsError ? 'alert' : 'status'}>{footer}</p>}
      </form>
    </div>
  );
}
